import React, { useEffect, useMemo, useState } from 'react';
import type { CSSProperties } from 'react';
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';

import TableStatus from '@/components/UI/TableStatus';
import { Flex } from '@/layout/Flex';
import { setSelectedId } from '@/redux/dataTable/dataTable.slice';
import { useAppDispatch } from '@/redux/store.hook';
import type { TableRequestState } from '@/types/table';

import FileMenu from './FileMenu';
import GlobalFilterInput from './GlobalFilterInput';
import SelectColumn from './SelectColumn';
import Tag from './Tag';
import TypeColumn from './TypeColumn';
import { Pagination, StyledTable, TableScroll } from './styles';

interface DataTableProps<T extends {}> {
  columns: ColumnDef<T>[];
  data: T[];
  reduxKey?: string;
  initSelected?: { [x: string]: boolean };
  canSelected?: boolean;
  canSearch?: boolean;
  setPerPage?: React.Dispatch<React.SetStateAction<number>>;
  setPage?: React.Dispatch<React.SetStateAction<number>>;
  page?: number;
  perPage?: number;
  total?: number;
  hidePagination?: boolean;
  mobileCardLayout?: boolean;
  requestState?: TableRequestState;
  loadingLabel?: string;
  refreshingLabel?: string;
  emptyLabel?: string;
  errorLabel?: string;
  retryLabel?: string;
  onRetry?: () => void;
}

type DataTableColumnMeta = {
  headerClassName?: string;
  headerStyle?: CSSProperties;
  cellClassName?: string;
  cellStyle?: CSSProperties;
  mobileLabel?: string;
};

const getColumnMeta = (meta: unknown) => meta as DataTableColumnMeta | undefined;

const DataTable = <T extends {}>({
  columns,
  data,
  reduxKey,
  initSelected = {},
  canSelected = false,
  canSearch = false,
  setPerPage = () => 10,
  setPage = () => 1,
  total,
  page = 1,
  perPage = 10,
  hidePagination = false,
  mobileCardLayout = false,
  requestState = { status: 'success', error: null },
  loadingLabel,
  refreshingLabel,
  emptyLabel,
  errorLabel,
  retryLabel,
  onRetry,
}: DataTableProps<T & { id?: string }>) => {
  // @ts-ignore
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [rowSelection, setRowSelection] = useState({ ...initSelected });
  const [globalFilter, setGlobalFilter] = useState('');
  const [sorting, setSorting] = useState<SortingState>([]);

  const table = useReactTable({
    autoResetAll: false,
    data,
    columns,
    initialState: { rowSelection },
    state: { rowSelection, globalFilter, sorting },
    onRowSelectionChange: setRowSelection,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onGlobalFilterChange: setGlobalFilter,
  });

  const selectedRowModel = table.getSelectedRowModel();
  const selectedRowData = useMemo(() => selectedRowModel.flatRows, [selectedRowModel]);
  const tableMinWidth = table.getVisibleLeafColumns().reduce((width, column) => width + column.getSize(), 0);
  const visibleColumnCount = Math.max(1, table.getVisibleLeafColumns().length);
  const visibleRows = table.getRowModel().rows;
  const hasRows = visibleRows.length > 0;
  const isBusy = requestState.status === 'loading' || requestState.status === 'refreshing';
  const isRefreshing = requestState.status === 'refreshing';
  const isInitialError = requestState.status === 'error' && (!requestState.hasStaleData || !hasRows);
  const areRequestControlsDisabled = isBusy || isInitialError;
  const isStaleError = requestState.status === 'error' && requestState.hasStaleData && hasRows;
  const showInlinePending = requestState.status === 'loading' || (isRefreshing && !hasRows);
  const showEmpty = requestState.status === 'success' && !hasRows;
  const showPagination = !hidePagination && !showInlinePending && !isInitialError;
  const serverPageCount = total === undefined ? undefined : Math.max(1, Math.ceil(total / perPage));

  const resolvedLoadingLabel = loadingLabel ?? (t('table.status.loading', { defaultValue: 'Loading data…' }) as string);
  const resolvedRefreshingLabel =
    refreshingLabel ?? (t('table.status.refreshing', { defaultValue: 'Updating data…' }) as string);
  const resolvedEmptyLabel =
    emptyLabel ?? (t('table.status.empty', { defaultValue: 'No matching data found.' }) as string);
  const resolvedErrorLabel =
    errorLabel ?? (t('table.status.error', { defaultValue: 'Unable to load data. Please try again.' }) as string);
  const resolvedRetryLabel = retryLabel ?? (t('table.actions.retry', { defaultValue: 'Retry' }) as string);

  useEffect(() => {
    table.setPageSize(perPage);
  }, [perPage, table]);

  useEffect(() => {
    if (!reduxKey) return;
    const selectedRowIds = selectedRowData.map((row) => row.id);
    const selectedDataIds = selectedRowData.map((row) => row.original.id as string);
    dispatch(setSelectedId({ selectedDataIds, selectedRowIds, key: reduxKey }));
  }, [dispatch, reduxKey, selectedRowData]);

  const bodyStatus = showInlinePending ? (
    <TableStatus
      status={requestState.status === 'refreshing' ? 'refreshing' : 'loading'}
      message={requestState.status === 'refreshing' ? resolvedRefreshingLabel : resolvedLoadingLabel}
    />
  ) : isInitialError ? (
    <TableStatus status="error" message={resolvedErrorLabel} retryLabel={resolvedRetryLabel} onRetry={onRetry} />
  ) : showEmpty ? (
    <TableStatus status="empty" message={resolvedEmptyLabel} />
  ) : null;

  const firstPageLabel = t('table.pagination.firstPage', { defaultValue: 'First page' }) as string;
  const previousPageLabel = t('table.pagination.previousPage', { defaultValue: 'Previous page' }) as string;
  const nextPageLabel = t('table.pagination.nextPage', { defaultValue: 'Next page' }) as string;
  const lastPageLabel = t('table.pagination.lastPage', { defaultValue: 'Last page' }) as string;

  return (
    <StyledTable $mobileCardLayout={mobileCardLayout} $isRefreshing={isRefreshing}>
      <Flex justifyContent="space-between" alignItems="center">
        <div>
          {canSelected && (
            <>
              {t('buttons.chosen')}：{Object.keys(rowSelection).length}
            </>
          )}
        </div>
        {canSearch && (
          <GlobalFilterInput
            value={globalFilter ?? ''}
            onChange={(value) => setGlobalFilter(String(value))}
            placeholder={t('info.searchAllColumns')}
            disabled={areRequestControlsDisabled}
          />
        )}
      </Flex>

      {isRefreshing && hasRows && <TableStatus status="refreshing" message={resolvedRefreshingLabel} compact />}
      {isStaleError && (
        <TableStatus
          status="error"
          message={resolvedErrorLabel}
          retryLabel={resolvedRetryLabel}
          onRetry={onRetry}
          compact
        />
      )}

      <TableScroll
        $mobileCardLayout={mobileCardLayout}
        style={{ '--data-table-min-width': `${tableMinWidth}px` } as CSSProperties}
        role="region"
        aria-label={t('info.dataTable')}
        aria-busy={isBusy}
        tabIndex={0}
      >
        <table>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const meta = getColumnMeta(header.column.columnDef.meta);
                  const canSort = header.column.getCanSort();
                  const sortState = header.column.getIsSorted();

                  return (
                    <th
                      key={header.id}
                      colSpan={header.colSpan}
                      className={meta?.headerClassName}
                      style={meta?.headerStyle}
                      aria-sort={sortState === 'asc' ? 'ascending' : sortState === 'desc' ? 'descending' : undefined}
                    >
                      <div
                        onClick={
                          canSort && !areRequestControlsDisabled ? header.column.getToggleSortingHandler() : undefined
                        }
                        className={canSort && !areRequestControlsDisabled ? 'pointer' : ''}
                        aria-disabled={canSort && areRequestControlsDisabled ? 'true' : undefined}
                      >
                        {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                        {canSort && (
                          <span className="sort-icon" aria-hidden="true">
                            {{ asc: <div className="asc" />, desc: <div className="desc" /> }[sortState as string] ??
                              null}
                          </span>
                        )}
                      </div>
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {bodyStatus ? (
              <tr className="table-status-row">
                <td colSpan={visibleColumnCount}>{bodyStatus}</td>
              </tr>
            ) : (
              visibleRows.map((row) => (
                <tr
                  key={row.id}
                  className={row.getIsSelected() ? 'selected' : ''}
                  aria-disabled={isRefreshing ? 'true' : undefined}
                >
                  {row.getVisibleCells().map((cell) => {
                    const meta = getColumnMeta(cell.column.columnDef.meta);
                    return (
                      <td
                        key={cell.id}
                        className={meta?.cellClassName}
                        style={meta?.cellStyle}
                        data-label={
                          meta?.mobileLabel ||
                          (typeof cell.column.columnDef.header === 'string' ? cell.column.columnDef.header : undefined)
                        }
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </TableScroll>

      {showPagination && (
        <Pagination $mobileCardLayout={mobileCardLayout}>
          <span className="total">
            {t('info.entriesInTotal')}: {total ?? table.getFilteredRowModel().rows.length}
          </span>
          <div className="pages">
            <label htmlFor="table-pagination" className="display">
              {t('info.display')}
              <select
                id="table-pagination"
                value={perPage}
                disabled={isBusy}
                onChange={(event) => {
                  table.setPageSize(Number(event.target.value));
                  setPerPage(Number(event.target.value));
                  setPage(1);
                }}
              >
                {[10, 20, 30, 40, 50].map((pageSize) => (
                  <option key={pageSize} value={pageSize}>
                    {pageSize}
                  </option>
                ))}
              </select>
            </label>
            {total !== undefined ? (
              <div className="change-page">
                <button
                  type="button"
                  aria-label={firstPageLabel}
                  onClick={() => setPage(1)}
                  disabled={isBusy || page <= 1}
                >
                  «
                </button>
                <button
                  type="button"
                  aria-label={previousPageLabel}
                  onClick={() => setPage(page - 1)}
                  disabled={isBusy || page <= 1}
                >
                  ‹
                </button>
                <button
                  type="button"
                  aria-label={nextPageLabel}
                  onClick={() => setPage(page + 1)}
                  disabled={isBusy || page >= (serverPageCount ?? 1)}
                >
                  ›
                </button>
                <button
                  type="button"
                  aria-label={lastPageLabel}
                  onClick={() => setPage(serverPageCount ?? 1)}
                  disabled={isBusy || page >= (serverPageCount ?? 1)}
                >
                  »
                </button>
                <span>
                  <span>{t('info.page')}</span>
                  <strong>
                    {page} of {serverPageCount}
                  </strong>
                </span>
                <label>
                  | {t('info.goToPage')}:
                  <input
                    type="number"
                    value={page}
                    min={1}
                    max={serverPageCount}
                    disabled={isBusy}
                    onChange={(event) => setPage(event.target.value ? Number(event.target.value) : 1)}
                  />
                </label>
              </div>
            ) : (
              <div className="change-page">
                <button
                  type="button"
                  aria-label={firstPageLabel}
                  onClick={() => table.setPageIndex(0)}
                  disabled={isBusy || !table.getCanPreviousPage()}
                >
                  «
                </button>
                <button
                  type="button"
                  aria-label={previousPageLabel}
                  onClick={() => table.previousPage()}
                  disabled={isBusy || !table.getCanPreviousPage()}
                >
                  ‹
                </button>
                <button
                  type="button"
                  aria-label={nextPageLabel}
                  onClick={() => table.nextPage()}
                  disabled={isBusy || !table.getCanNextPage()}
                >
                  ›
                </button>
                <button
                  type="button"
                  aria-label={lastPageLabel}
                  onClick={() => table.setPageIndex(Math.max(0, table.getPageCount() - 1))}
                  disabled={isBusy || !table.getCanNextPage()}
                >
                  »
                </button>
                <span>
                  <span>{t('info.page')}</span>
                  <strong>
                    {table.getState().pagination.pageIndex + 1} of {Math.max(1, table.getPageCount())}
                  </strong>
                </span>
                <label>
                  | {t('info.goToPage')}:
                  <input
                    type="number"
                    value={table.getState().pagination.pageIndex + 1}
                    min={1}
                    max={Math.max(1, table.getPageCount())}
                    disabled={isBusy}
                    onChange={(event) => table.setPageIndex(event.target.value ? Number(event.target.value) - 1 : 0)}
                  />
                </label>
              </div>
            )}
          </div>
        </Pagination>
      )}
    </StyledTable>
  );
};

export default DataTable;
export { FileMenu, SelectColumn, Tag, TypeColumn };
